import Close20 from '@carbon/icons-react/es/close/20';
import StarFilled20 from '@carbon/icons-react/es/star--filled/20';
import { Space } from 'antd';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation } from 'react-query';
import styled from 'styled-components';
import { TaskAPI } from 'api/tasks';
import { Button } from 'components/atoms/button';
import { TextArea } from 'components/atoms/input';
import { Rate as RateAtom } from 'components/atoms/rate';
import { CallToAction } from 'components/molecules/call-to-action';
import {
  ProductiveHeading05,
  ProductiveHeading06,
  Text01,
} from 'components/styles';
import { useInput } from 'hooks/use-input';
import { Hierarchy } from './hierarchy';

interface CsatFormProps {
  taskId: number;
  projectName?: string;
  projectPhaseName?: string;
  taskName: string;
  onCancel: () => void;
  onOk: () => void;
}

const Container = styled(Space)`
  width: 446px;
`;

const Rate = styled(RateAtom)`
  .ant-rate-star.ant-rate-star-full {
    .star-box {
      background-color: var(--private-02);
    }
  }
`;

const Heading = styled.div`
  ${ProductiveHeading05};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StarBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  width: 80px;
  background-color: var(--coolest-gray-10);
`;

const RatingText = styled.div`
  ${ProductiveHeading06};
  ${Text01};
  min-height: 22px;
  text-align: center;
  display: flex;
  align-content: center;
  justify-content: center;
  margin: var(--spacing-05) 0;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: var(--layout-02);
`;

const RateContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const CsatForm: FC<CsatFormProps> = (props) => {
  const { taskId, taskName, projectName, projectPhaseName, onCancel, onOk } =
    props;
  const [tempRating, setTempRating] = useState<number | undefined>(0);
  const [rating, setRating] = useState(0);
  const [comment, onChange, setComment] = useInput('');

  const clearState = () => {
    setComment('');
    setRating(0);
    setTempRating(0);
  };

  const addRating = () => TaskAPI.addRating(taskId, { rating, comment });
  const { mutateAsync: submit, isLoading: loading } = useMutation(addRating, {
    onSuccess: () => {
      clearState();
      onOk();
    },
  });

  const handleCancel = () => {
    clearState();
    onCancel();
  };

  return (
    <Container direction="vertical">
      <Hierarchy
        projectName={projectName}
        projectPhaseName={projectPhaseName}
      />
      <Heading>
        <FormattedMessage id="rate_your_experience" />
        <Button type="text" icon={<Close20 />} onClick={handleCancel} />
      </Heading>
      <div>
        <FormattedMessage id="csat.question" values={{ name: taskName }} />
      </div>
      <RateContainer>
        <Rate
          onHoverChange={setTempRating}
          onChange={setRating}
          allowHalf={false}
          value={rating}
          defaultValue={0}
          character={
            <StarBox className="star-box">
              <StarFilled20 />
            </StarBox>
          }
        />
      </RateContainer>
      <RatingText>
        {rating || tempRating ? (
          <FormattedMessage id={`csat.rating.${tempRating || rating}`} />
        ) : null}
      </RatingText>

      <FormattedMessage id="csat.form.message" />
      <TextArea
        value={comment}
        onChange={onChange}
        autoSize={{ minRows: 4, maxRows: 4 }}
        bordered={false}
      />
      <Footer>
        <CallToAction
          align="end"
          okButtonProps={{ disabled: !rating, loading }}
          onOk={submit}
          onCancel={handleCancel}
        />
      </Footer>
    </Container>
  );
};
